import type { Candidate } from '@shouyi/shared';

import { displayDate, displayNumber } from './format.js';

export type RiskTone = 'low' | 'medium' | 'high' | 'unknown';
type ModelReadinessSnapshot = Pick<import('@shouyi/shared').QuantSnapshot, 'candidates' | 'productionReady'>;

export type ModelReadinessNotice = {
  label: string;
  message: string;
};

export function normalizeCandidateSearch(value: string): string {
  return value.normalize('NFKC').replace(/\s+/g, '').toLowerCase();
}

export function findCandidate(candidates: Candidate[], value: string): Candidate | undefined {
  const query = normalizeCandidateSearch(value);
  if (!query) return undefined;
  return candidates.find((row) => (
    row.code === query
    || normalizeCandidateSearch(row.name).includes(query)
    || normalizeCandidateSearch(row.industry).includes(query)
  ));
}

export function riskTone(riskTag: string): RiskTone {
  if (riskTag.includes('低')) return 'low';
  if (riskTag.includes('中')) return 'medium';
  if (riskTag.includes('高')) return 'high';
  return 'unknown';
}

export function riskLabel(riskTag: string): string {
  const normalized = riskTag.trim();
  if (!normalized || normalized === '未知') return '风险待评估';
  return normalized.startsWith('风险') ? normalized : `风险${normalized}`;
}

export function validatedT5ExcessProbability(candidate: Candidate): number | null {
  if (candidate.modelStatus !== 'champion') return null;
  const value = candidate.modelT5ExcessProbability ?? candidate.modelT5Probability;
  if (value === null || !Number.isFinite(value) || value < 0 || value > 1) return null;
  return value;
}

export function t5ProbabilityEvidenceLabel(candidate: Candidate): string {
  if (candidate.modelStatus !== 'champion') return '尚无合格冠军模型';
  return validatedT5ExcessProbability(candidate) === null ? '冠军模型概率证据缺失' : '时间外校准结果';
}

export function modelReadinessNotice(snapshot: ModelReadinessSnapshot | null): ModelReadinessNotice {
  if (!snapshot) {
    return { label: '数据等待：', message: '等待首个通过结构校验的不可变量化快照。' };
  }
  const hasChampion = snapshot.candidates.some((row) => row.modelStatus === 'champion');
  if (!hasChampion) {
    return {
      label: '研究阶段提示：',
      message: '当前尚无经生产验证的冠军模型，量化排序不等于上涨概率，候选均需进入 T+5 前向观察。'
    };
  }
  if (!snapshot.productionReady) {
    return {
      label: '验证阶段提示：',
      message: '已有经时间外验证的冠军模型，但整体策略尚未通过生产准入；仅对证据完整的候选展示校准概率。'
    };
  }
  return {
    label: '生产准入提示：',
    message: '冠军模型与整体策略准入门均已通过；概率仍是统计估计，不构成收益承诺或交易建议。'
  };
}

export function candidateReasons(candidate: Candidate, peers: Candidate[]): string[] {
  const reasons: string[] = [];
  if (candidate.reason.trim()) reasons.push(candidate.reason.trim());

  reasons.push(`综合量化得分 ${displayNumber(candidate.quantScore)}，本期排序第 ${candidate.rank}`);

  if (candidate.factorScore !== null) {
    const peerFactors = peers.map((row) => row.factorScore).filter((value): value is number => value !== null);
    const isPeerHigh = peerFactors.length > 1 && candidate.factorScore === Math.max(...peerFactors);
    reasons.push(isPeerHigh
      ? `多因子得分 ${displayNumber(candidate.factorScore)}，为当前比较候选最高`
      : `多因子得分 ${displayNumber(candidate.factorScore)}，系统${riskLabel(candidate.riskTag)}`);
  } else {
    reasons.push(`${candidate.industry}行业候选，系统${riskLabel(candidate.riskTag)}`);
  }

  reasons.push(`行情证据截至 ${displayDate(candidate.quoteTradeDate)}，进入 T+5 前向观察`);
  return [...new Set(reasons)].slice(0, 3);
}

export function filterCandidates(candidates: Candidate[], filters: { query: string; industry: string; risk: string; sort: string }): Candidate[] {
  const query = normalizeCandidateSearch(filters.query);
  return candidates.filter((row) => (
    (!query || [row.code, row.name, row.industry, row.strategy].some((value) => normalizeCandidateSearch(value).includes(query)))
    && (!filters.industry || row.industry === filters.industry)
    && (!filters.risk || riskTone(row.riskTag) === filters.risk)
  )).sort((a, b) => {
    if (filters.sort === 'factor') return (b.factorScore ?? -Infinity) - (a.factorScore ?? -Infinity) || a.rank - b.rank;
    if (filters.sort === 'quant') return b.quantScore - a.quantScore || a.rank - b.rank;
    return a.rank - b.rank;
  });
}
